import { differenceInCalendarDays } from 'date-fns'
import sortBy from 'lodash/sortBy'
import { experimentManager } from 'services/ExperimentManager/ExperimentManager'
import { hasHolidayBetweenArrivals } from 'services/HolidayService'
import { isoToDeliveryDate } from 'utils/DateTimeUtils'

const MAX_SHARED_PROMISE_GAP_DAYS = 4

type Translate = (key: string, options?: Record<string, string>) => string

export interface ProductArrivals {
  tilesArrivalIso: string
  photoBookArrivalIso: string
}

export const PRODUCT_ARRIVALS_SPLIT_REASONS = {
  ARRIVAL_GAP: 'Arrival Gap',
  HOLIDAY_BETWEEN: 'Holiday Between',
  WITHIN_RANGE: 'Within Range',
} as const

type ProductArrivalsSplitReason =
  (typeof PRODUCT_ARRIVALS_SPLIT_REASONS)[keyof typeof PRODUCT_ARRIVALS_SPLIT_REASONS]

interface GetProductArrivalsSplitReasonParams {
  productArrivals: ProductArrivals
  country: string | undefined
}

export function getProductArrivalsSplitReason({
  productArrivals,
  country,
}: GetProductArrivalsSplitReasonParams): ProductArrivalsSplitReason {
  const [earlierArrivalIso, laterArrivalIso] = sortBy(
    [productArrivals.tilesArrivalIso, productArrivals.photoBookArrivalIso],
    (arrivalIso) => new Date(arrivalIso).getTime()
  )
  if (
    differenceInCalendarDays(
      new Date(laterArrivalIso),
      new Date(earlierArrivalIso)
    ) > MAX_SHARED_PROMISE_GAP_DAYS
  ) {
    return PRODUCT_ARRIVALS_SPLIT_REASONS.ARRIVAL_GAP
  }
  if (
    hasHolidayBetweenArrivals({ earlierArrivalIso, laterArrivalIso, country })
  ) {
    return PRODUCT_ARRIVALS_SPLIT_REASONS.HOLIDAY_BETWEEN
  }
  return PRODUCT_ARRIVALS_SPLIT_REASONS.WITHIN_RANGE
}

export function shouldShowSeparateProductArrivals({
  productArrivals,
  country,
}: GetProductArrivalsSplitReasonParams): boolean {
  const isSplitEligible =
    getProductArrivalsSplitReason({ productArrivals, country }) !==
    PRODUCT_ARRIVALS_SPLIT_REASONS.WITHIN_RANGE
  // The experiment is checked only for carts that would split, so its exposures count only
  // customers who could see a difference
  return (
    isSplitEligible &&
    experimentManager.isEnabled('cart-separate-promise-dates')
  )
}

function getProductArrivalText(
  translate: Translate,
  productNameKey: string,
  arrivalIso: string
): string {
  const productName = translate(productNameKey)
  const estimatedDelivery = translate(
    'order.checkout.shipping_options.estimated_delivery',
    { v1: isoToDeliveryDate(arrivalIso, 'mmm d') }
  )
  return `${productName}: ${estimatedDelivery}`
}

export function getProductArrivalTexts(
  translate: Translate,
  { tilesArrivalIso, photoBookArrivalIso }: ProductArrivals
) {
  return {
    tilesArrivalText: getProductArrivalText(
      translate,
      'product_selection.tiles_name',
      tilesArrivalIso
    ),
    photoBookArrivalText: getProductArrivalText(
      translate,
      'gift_card.books',
      photoBookArrivalIso
    ),
  }
}
